import type { ComponentType, ReactNode } from "react"

import {
  mockPage,
  sampleAvailableLocations,
  sampleCurrentLocation,
  sampleUser,
} from "@/components/layouts/storybook-helpers"

type PageComponent = ComponentType<any>

type PageStoryOptions = {
  name?: string
  url: string
  componentProps?: Record<string, unknown>
  pageProps?: Record<string, unknown>
  wrap?: (page: ReactNode) => ReactNode
}

export function createPageStory(Component: PageComponent, options: PageStoryOptions) {
  const componentProps = options.componentProps ?? {}
  const pageProps = options.pageProps ?? componentProps

  return {
    name: options.name,
    beforeEach: () => {
      mockPage({
        url: options.url,
        props: pageProps,
      })
    },
    render: () => {
      const page = <Component {...componentProps} />

      return <>{options.wrap ? options.wrap(page) : page}</>
    },
  }
}

export const fixedNow = "2026-06-26T16:30:00.000Z"

export const adminPageProps = {
  user: sampleUser,
}

export const staffPageProps = {
  user: sampleUser,
  currentLocation: sampleCurrentLocation,
  availableLocations: sampleAvailableLocations,
  locationUser: {
    // The stored enum value, lowercase. `SidebarNav` compares against
    // `"manager"`, so a capitalised sample renders every staff story with the
    // management nav missing — which is not what any of these screens look like.
    role: "manager",
  },
  // A restaurant with the operational features on and the platform-gated ones
  // off, which is what most tenants look like. `SidebarNav` hides a group whose
  // items are all gated off, so this is also what makes the grouping visible.
  enabledFeatures: ["inventory", "purchasing", "promotions", "loyalty"],
  hasLocations: true,
  flash: {},
}

export const userPageProps = {
  user: sampleUser,
  cartCount: 2,
}

/**
 * A pantry mid-service: one supply below its reorder point, one in negative, one
 * that has never moved (so it is still deletable and its unit is still open).
 */
export const sampleStaffSupplyUnits = [
  { value: "gram", label: "Gramo" },
  { value: "kilogram", label: "Kilogramo" },
  { value: "milliliter", label: "Mililitro" },
  { value: "liter", label: "Litro" },
  { value: "piece", label: "Pieza" },
]

export const sampleStaffSupplies = [
  {
    id: 1,
    name: "Jitomate",
    unit: "kilogram",
    unitLabel: "Kilogramo",
    stockOnHand: "7.500",
    reorderPoint: "10.000",
    short: false,
    belowReorderPoint: true,
    unitLocked: true,
    destroyable: false,
  },
  {
    id: 2,
    name: "Queso Oaxaca",
    unit: "kilogram",
    unitLabel: "Kilogramo",
    stockOnHand: "-1.250",
    reorderPoint: "4.000",
    short: true,
    belowReorderPoint: true,
    unitLocked: true,
    destroyable: false,
  },
  {
    id: 3,
    name: "Servilletas",
    unit: "piece",
    unitLabel: "Pieza",
    stockOnHand: "500.000",
    reorderPoint: null,
    short: false,
    belowReorderPoint: false,
    unitLocked: false,
    destroyable: true,
  },
]

/** The ledger the drawer shows for supply 1, as the partial reload returns it. */
export const sampleStaffSupplyLedger = {
  ...sampleStaffSupplies[0],
  movements: [
    {
      id: 31,
      reason: "recipe_consumption",
      reasonLabel: "Consumo por receta",
      quantity: "-2.500",
      balanceAfter: "7.500",
      note: null,
      createdAt: fixedNow,
      actorName: null,
      orderId: 918,
    },
    {
      id: 30,
      reason: "purchase",
      reasonLabel: "Compra",
      quantity: "10.000",
      balanceAfter: "10.000",
      note: "F-2291",
      createdAt: "2026-06-25T15:00:00.000Z",
      actorName: "Ana Ruiz",
      orderId: null,
    },
  ],
}

export const samplePagination = {
  page: 1,
  perPage: 10,
  totalCount: 24,
  totalPages: 3,
  hasPrevious: false,
  hasNext: true,
  previousPage: null,
  nextPage: 2,
}

export const sampleAdminEventRows = [
  {
    id: 9401,
    locationId: 42,
    action: "location_created",
    aggregateType: "Location",
    aggregateId: 42,
    aggregate: "Location#42",
    eventName: "Locations::Created",
    createdAt: fixedNow,
  },
  {
    id: 9400,
    locationId: null,
    action: "user_confirmed",
    aggregateType: "User",
    aggregateId: 17,
    aggregate: "User#17",
    eventName: "Users::Confirmed",
    createdAt: "2026-06-26T15:45:00.000Z",
  },
]

export const sampleAdminLocationRows = [
  {
    id: 42,
    name: "Menny Roma Norte",
    status: "active",
    statusLabel: "Activa",
    offlineCommissionPercentage: "1.0",
    digitalCommissionPercentage: "1.0",
    staffCount: 8,
    tableCount: 18,
    createdAt: "2026-04-12T10:00:00.000Z",
  },
  {
    id: 43,
    name: "Menny Condesa",
    status: "inactive",
    statusLabel: "Pendiente",
    offlineCommissionPercentage: "1.0",
    digitalCommissionPercentage: "1.0",
    staffCount: 3,
    tableCount: 10,
    createdAt: "2026-05-03T12:20:00.000Z",
  },
]

export const sampleAdminLocationFormValue = {
  id: 42,
  name: "Menny Roma Norte",
  status: "active",
  timeZone: "America/Mexico_City",
  offlineCommissionPercentage: "1.0",
  digitalCommissionPercentage: "1.0",
}

export const sampleNewAdminLocationFormValue = {
  id: null,
  name: "",
  status: "inactive",
  timeZone: "America/Mexico_City",
  offlineCommissionPercentage: "1.0",
  digitalCommissionPercentage: "1.0",
}

export const sampleLocationStatusOptions = [
  { value: "inactive", label: "Pendiente" },
  { value: "active", label: "Activa" },
  { value: "suspended", label: "Suspendida" },
]

export const sampleAdminLocationDetails = {
  id: 42,
  status: "active",
  statusLabel: "Activa",
  timeZone: "America/Mexico_City",
  address: "Álvaro Obregón 106, Roma Norte, CDMX, 06700, MX",
  organizationName: "Grupo Menny",
  createdAt: "2026-05-03T12:20:00.000Z",
  updatedAt: fixedNow,
}

/**
 * Two sections, and inside them the three states the admin feature cards have to
 * tell apart: granted and running, granted but switched off by the restaurant,
 * and not granted at all.
 */
export const sampleAdminLocationFeatureSections = [
  {
    group: "service",
    label: "Servicio",
    features: [
      {
        key: "waiter_call",
        label: "Llamar al mesero",
        description: "El comensal puede pedir atención desde su mesa.",
        available: true,
        enabled: true,
      },
      {
        key: "pickup_orders",
        label: "Pedidos para llevar",
        description: "Permite que tus comensales pidan por adelantado y pasen a recoger.",
        available: true,
        enabled: false,
      },
      {
        key: "public_menu",
        label: "Menú público",
        description: "Dirección pública del menú en /r/:slug.",
        available: false,
        enabled: false,
      },
    ],
  },
  {
    group: "operation",
    label: "Operación",
    features: [
      {
        key: "cash_register",
        label: "Caja",
        description: "Cortes de caja y movimientos de efectivo por turno.",
        available: true,
        enabled: true,
      },
      {
        key: "printers",
        label: "Impresoras",
        description: "Comandas impresas por estación.",
        available: false,
        enabled: false,
      },
    ],
  },
]

/**
 * Three rows that cover the three answers the transactions screen gives: Stripe
 * refused, Stripe charged, and the attempt never reached Stripe at all. The
 * third is a cash payment, which is supposed to look like that.
 */
export const sampleAdminTransactionRows = [
  {
    id: 5102,
    status: "failed",
    statusLabel: "Fallido",
    paymentMethod: "digital_in_app",
    paymentMethodLabel: "Pago digital",
    totalAmount: "348.00",
    commissionTotalAmount: "3.48",
    createdAt: fixedNow,
    failureReason: "No such account: acct_rest_123",
    location: { id: 42, name: "Taquería La Once" },
    diner: { id: 17, name: "Mariana López", email: "mariana@menny.app" },
    billId: 8801,
    providerStatus: "Fallida",
  },
  {
    id: 5101,
    status: "succeeded",
    statusLabel: "Exitoso",
    paymentMethod: "digital_in_app",
    paymentMethodLabel: "Pago digital",
    totalAmount: "512.50",
    commissionTotalAmount: "5.13",
    createdAt: "2026-06-26T15:12:00.000Z",
    failureReason: null,
    location: { id: 43, name: "El Califa" },
    diner: { id: 18, name: "Juan Pérez", email: "juan@menny.app" },
    billId: 8800,
    providerStatus: "Exitosa",
  },
  {
    id: 5100,
    status: "succeeded",
    statusLabel: "Exitoso",
    paymentMethod: "cash",
    paymentMethodLabel: "Efectivo",
    totalAmount: "180.00",
    commissionTotalAmount: "1.80",
    createdAt: "2026-06-26T14:02:00.000Z",
    failureReason: null,
    location: { id: 42, name: "Taquería La Once" },
    diner: { id: 19, name: "Ana Ruiz", email: "ana@menny.app" },
    billId: 8799,
    providerStatus: null,
  },
]

export const sampleAdminTransactionStatusOptions = [
  { value: "pending", label: "Pendiente" },
  { value: "succeeded", label: "Exitoso" },
  { value: "failed", label: "Fallido" },
  { value: "canceled", label: "Cancelado" },
  { value: "processing", label: "Procesando" },
  { value: "requires_action", label: "Requiere acción" },
]

export const sampleAdminTransactionPaymentMethodOptions = [
  { value: "cash", label: "Efectivo" },
  { value: "physical_card_terminal", label: "Tarjeta física" },
  { value: "digital_in_app", label: "Pago digital" },
]

/** The failed pickup checkout, which is the case the detail screen was built for. */
export const sampleAdminTransactionDetail = {
  ...sampleAdminTransactionRows[0],
  kind: "pickup" as const,
  idempotencyKey: "9f2c1ab84e5d47c0b1e6a3f7d90c25be4a18f6d3c0b7e2a591d4c8f03b6e7a12",
  selectedOrderIds: [],
  selectedSubtotal: "348.00",
  tipAmount: "0.00",
  commissionPercentage: "1.00",
  completedAt: fixedNow,
  billSplitId: null,
  bill: { id: 8801, status: "awaiting_payment", statusLabel: "Esperando pago" },
  snapshot: {
    pickup: {
      user_id: 17,
      lines: [
        { item_id: 301, quantity: 2, price: "119.00", notes: "Sin cebolla" },
        { item_id: 318, quantity: 1, price: "110.00" },
      ],
    },
  },
  provider: {
    id: 7701,
    provider: "stripe",
    status: "failed",
    statusLabel: "Fallida",
    providerStatus: null,
    currency: "mxn",
    amountTotalCents: 34_800,
    applicationFeeAmountCents: 348,
    connectedAccountId: "acct_rest_123",
    checkoutSessionId: null,
    paymentIntentId: null,
    chargeId: null,
    refundId: null,
    receiptUrl: null,
    lastProviderError: "No such account: acct_rest_123",
    checkoutExpiresAt: null,
    completedAt: null,
    createdAt: fixedNow,
  },
}

export const sampleAdminUsers = [
  {
    id: 17,
    name: "Mariana López",
    email: "mariana@menny.app",
    confirmed: true,
    confirmedAt: "2026-04-01T08:00:00.000Z",
    lastActivityAt: fixedNow,
    createdAt: "2026-03-14T13:00:00.000Z",
  },
  {
    id: 18,
    name: "Diego Ramos",
    email: "diego@menny.app",
    confirmed: false,
    confirmedAt: null,
    lastActivityAt: null,
    createdAt: "2026-06-01T09:30:00.000Z",
  },
]

export const sampleAdminJobsDashboard = {
  generatedAt: fixedNow,
  databaseAvailable: true,
  missingTables: [],
  schemaSummary: [
    {
      table: "solid_queue_jobs",
      purpose: "Jobs persistidos para ejecución asincrónica",
      available: true,
    },
    {
      table: "solid_queue_processes",
      purpose: "Workers activos y heartbeat operativo",
      available: true,
    },
  ],
  overview: {
    totalJobs: 128,
    queuedJobs: 6,
    runningJobs: 2,
    scheduledJobs: 4,
    blockedJobs: 1,
    failedJobs: 3,
    finishedJobs: 116,
    pausedQueues: 0,
    activeProcesses: 3,
    staleProcesses: 0,
    jobsCreatedLast24h: 37,
    failedLast24h: 1,
  },
  queues: [
    {
      queueName: "default",
      queued: 4,
      running: 1,
      scheduled: 2,
      blocked: 0,
      failed: 1,
      paused: false,
    },
    {
      queueName: "billing",
      queued: 2,
      running: 1,
      scheduled: 2,
      blocked: 1,
      failed: 2,
      paused: false,
    },
  ],
  recentJobs: [
    {
      id: 501,
      activeJobId: "2fd1",
      className: "Billing::CommissionInvoiceJob",
      queueName: "billing",
      priority: 0,
      status: "finished",
      createdAt: fixedNow,
      scheduledAt: null,
      finishedAt: fixedNow,
      failedMessage: null,
      processId: 21,
      concurrencyKey: null,
    },
  ],
  queuedExecutions: [
    {
      id: 77,
      className: "Notifications::BroadcastJob",
      queueName: "default",
      priority: 0,
      status: "queued",
      scheduledAt: null,
      enqueuedAt: fixedNow,
    },
  ],
  failedExecutions: [
    {
      id: 31,
      className: "Payments::ReconcileAttemptJob",
      queueName: "billing",
      failedAt: "2026-06-26T13:10:00.000Z",
      message: "Stripe account pending requirements",
      exceptionClass: "Payments::ProviderError",
      backtrace: ["app/jobs/payments/reconcile_attempt_job.rb:18"],
    },
  ],
  processes: [
    {
      id: 21,
      name: "worker.1",
      kind: "Worker",
      pid: 1142,
      hostname: "local",
      supervisorId: null,
      lastHeartbeatAt: fixedNow,
      stale: false,
    },
  ],
  recurringTasks: [
    {
      key: "billing.commissions.daily",
      className: "Billing::CommissionInvoiceJob",
      queueName: "billing",
      priority: 0,
      schedule: "every day at 02:00",
      static: true,
      description: "Genera facturas de comisión",
      lastEnqueuedAt: "2026-06-26T08:00:00.000Z",
      nextRunAt: "2026-06-27T08:00:00.000Z",
    },
  ],
  availableJobs: [
    {
      className: "Notifications::BroadcastJob",
      queueName: "default",
      namespace: "Notifications",
      performSignature: "perform(event_id)",
    },
  ],
  flash: {
    notice: {
      header: "Cola actualizada",
      description: "Datos de Solid Queue cargados para Storybook.",
    },
  },
}

export const sampleExtraGroups = [
  {
    id: 501,
    name: "Quesos",
    minSelect: 0,
    maxSelect: 2,
    status: "active",
    itemIds: ["101"],
    extras: [
      { id: 201, name: "Queso Oaxaca", price: "18.00", status: "active", imageUrl: null },
      { id: 202, name: "Salsa tatemada", price: "0.00", status: "active", imageUrl: null },
    ],
  },
]

export const sampleMenuItems = [
  {
    id: 101,
    name: "Taco pastor",
    description: "Tortilla de maíz, cerdo marinado, piña y cilantro.",
    price: "68.00",
    currency: "mxn",
    imageUrl: null,
    emoji: "🌮",
    status: "active",
    station: "kitchen",
    extraGroups: [
      {
        id: 501,
        name: "Quesos",
        minSelect: 0,
        maxSelect: 2,
        extras: [
          { id: 201, name: "Queso Oaxaca", price: "18.00", status: "active" },
          { id: 202, name: "Salsa tatemada", price: "0.00", status: "active" },
        ],
      },
    ],
  },
  {
    id: 102,
    name: "Agua de jamaica",
    description: "Refrescante, sin azúcar agregada.",
    price: "42.00",
    currency: "mxn",
    imageUrl: null,
    emoji: "🥤",
    status: "active",
    station: "bar",
    extraGroups: [],
  },
]

export const sampleMenuCategories = [
  {
    id: 1,
    name: "Favoritos",
    emoji: "⭐",
    items: sampleMenuItems,
  },
]

export const sampleOrphanMenuItems = [
  {
    id: 103,
    name: "Café de olla",
    description: "Canela, piloncillo y café de Veracruz.",
    price: "48.00",
    currency: "mxn",
    imageUrl: null,
    status: "active",
    station: "bar",
    extraGroups: [],
  },
]

export const sampleStaffEventRows = [
  {
    id: 8801,
    locationId: 42,
    action: "order_created",
    aggregateType: "Order",
    aggregateId: 7001,
    aggregate: "Order#7001",
    eventName: "Location::OrderCreated",
    createdAt: fixedNow,
  },
  {
    id: 8800,
    locationId: 42,
    action: "bill_closed",
    aggregateType: "Bill",
    aggregateId: 5001,
    aggregate: "Bill#5001",
    eventName: "Location::BillClosed",
    createdAt: "2026-06-26T15:10:00.000Z",
  },
]

export const sampleStatusColumns = [
  { value: "pending", label: "Pendientes" },
  { value: "in_progress", label: "En preparación" },
  { value: "ready", label: "Listas" },
  { value: "completed", label: "Completadas" },
]

export const sampleKanbanOrders = [
  {
    id: 7001,
    name: "Taco pastor",
    status: "pending",
    statusLabel: "Pendiente",
    serviceType: "eat_in",
    serviceTypeLabel: "Mesa",
    station: "kitchen",
    tableName: "Mesa 4",
    billId: 5001,
    customerName: "Lucía",
    quantity: 2,
    notes: "Sin cebolla",
    price: "68.00",
    currency: "mxn",
    modifiers: [{ id: 201, name: "Queso Oaxaca", price: "18.00" }],
    pickupEtaMinutes: null,
  },
  {
    id: 7002,
    name: "Agua de jamaica",
    status: "ready",
    statusLabel: "Lista",
    serviceType: "eat_in",
    serviceTypeLabel: "Mesa",
    station: "bar",
    tableName: "Mesa 4",
    billId: 5001,
    customerName: "Nicolás",
    quantity: 1,
    notes: "",
    price: "42.00",
    currency: "mxn",
    modifiers: [],
    pickupEtaMinutes: null,
  },
]

export const sampleTableOptions = [
  {
    id: 301,
    externalId: "tbl-4",
    name: "Mesa 4",
    status: "occupied",
    statusLabel: "Ocupada",
    activeBills: [{ id: 5001, label: "Cuenta #5001" }],
  },
  {
    id: 302,
    externalId: "tbl-7",
    name: "Mesa 7",
    status: "available",
    statusLabel: "Disponible",
    activeBills: [],
  },
]

export const sampleServiceTypes = [
  { value: "eat_in", label: "Mesa" },
  { value: "take_out", label: "Para llevar" },
  { value: "pick_up", label: "Pickup" },
]

export const samplePaymentBills = [
  {
    id: 5001,
    status: "open",
    tableNames: ["Mesa 4"],
    pendingPaymentAttempt: null,
    orders: [
      {
        id: 7001,
        name: "Taco pastor",
        quantity: 2,
        lineTotal: "136.00",
        paymentStatus: "unpaid",
        serviceType: "eat_in",
      },
    ],
  },
]

export const sampleStaffBills = [
  {
    id: 5001,
    status: "open",
    statusLabel: "Abierta",
    tableNames: ["Mesa 4"],
    ordersCount: 2,
    unpaidOrdersCount: 2,
    subtotal: "178.00",
    tip: "18.00",
    commission: "2.00",
    total: "198.00",
    createdAt: "2026-06-26T14:30:00.000Z",
    updatedAt: fixedNow,
    pendingPaymentAttempt: null,
    orders: [
      {
        id: 7001,
        name: "Taco pastor",
        quantity: 2,
        status: "pending",
        statusLabel: "Pendiente",
        paymentStatus: "unpaid",
        paymentStatusLabel: "Sin pagar",
        serviceType: "eat_in",
        serviceTypeLabel: "Mesa",
        lineTotal: "136.00",
        currency: "mxn",
        notes: "Sin cebolla",
        customerName: "Lucía",
      },
      {
        id: 7002,
        name: "Agua de jamaica",
        quantity: 1,
        status: "ready",
        statusLabel: "Lista",
        paymentStatus: "unpaid",
        paymentStatusLabel: "Sin pagar",
        serviceType: "eat_in",
        serviceTypeLabel: "Mesa",
        lineTotal: "42.00",
        currency: "mxn",
        notes: null,
        customerName: "Nicolás",
      },
    ],
    allowedActions: {
      canClose: true,
      canCreatePaymentAttempt: true,
      canResolvePendingAttempt: false,
    },
  },
]

export const sampleManageDashboardProps = {
  quickStats: [
    { id: "orders", label: "Comandas activas", value: 12, description: "Pendientes y en cocina" },
    { id: "tables", label: "Mesas ocupadas", value: 8, description: "Con cuentas abiertas" },
    { id: "bills", label: "Cuentas abiertas", value: 9, description: "Listas para seguimiento" },
    { id: "staff", label: "Staff activo", value: 6, description: "Turno de comida" },
  ],
  orderStatusCards: [
    { value: "pending", label: "Pendientes", count: 4 },
    { value: "in_progress", label: "En preparación", count: 5 },
    { value: "ready", label: "Listas", count: 3 },
  ],
  tableStatusCards: [
    { value: "available", label: "Disponibles", count: 10 },
    { value: "occupied", label: "Ocupadas", count: 8 },
  ],
  stationCards: [
    { value: "kitchen", label: "Cocina", count: 9 },
    { value: "bar", label: "Barra", count: 3 },
  ],
  recentOrders: [
    {
      id: 7001,
      name: "Taco pastor",
      statusLabel: "Pendiente",
      serviceTypeLabel: "Mesa",
      tableName: "Mesa 4",
      customerName: "Lucía",
      createdAt: fixedNow,
    },
  ],
}

export const sampleManageTables = [
  {
    id: 301,
    externalId: "tbl-4",
    name: "Mesa 4",
    seats: 4,
    status: "occupied",
    statusLabel: "Ocupada",
    hasActiveInvite: true,
    hasStaticInvite: true,
  },
  {
    id: 302,
    externalId: "tbl-7",
    name: "Mesa 7",
    seats: 2,
    status: "available",
    statusLabel: "Disponible",
    hasActiveInvite: false,
    hasStaticInvite: false,
  },
]

export const sampleTableStatuses = [
  { value: "available", label: "Disponible" },
  { value: "occupied", label: "Ocupada" },
  { value: "reserved", label: "Reservada" },
  { value: "disabled", label: "Deshabilitada" },
]

export const sampleActiveInvite = {
  id: 401,
  token: "tbl-4-token",
  expiresAt: "2026-06-26T20:30:00.000Z",
  maxUses: 8,
  usedCount: 2,
  url: "https://menny.app/i/tbl-4-token",
}

export const sampleStaticInvite = {
  id: 402,
  token: "tbl-4-static-token",
  url: "https://menny.app/i/tbl-4-static-token",
}

export const sampleManageTable = {
  id: 301,
  externalId: "tbl-4",
  name: "Mesa 4",
  seats: 4,
  status: "occupied",
  statusLabel: "Ocupada",
  activeBillsCount: 1,
  activeInvite: sampleActiveInvite,
  staticInvite: sampleStaticInvite,
}

export const sampleTableAccountOrder = {
  id: 7001,
  name: "Taco pastor",
  quantity: 2,
  status: "pending",
  statusLabel: "Pendiente",
  serviceType: "eat_in",
  serviceTypeLabel: "Mesa",
  lineTotal: "136.00",
  currency: "mxn",
  notes: "Sin cebolla",
  customerName: "Lucía",
  updatedAt: fixedNow,
  billId: 5001,
  billLabel: "Cuenta #5001",
  billStatus: "open",
  billStatusLabel: "Abierta",
}

export const sampleTableAccountBill = {
  id: 5001,
  label: "Cuenta #5001",
  status: "open",
  statusLabel: "Abierta",
  canClose: true,
  pendingPaymentAttempt: null,
  allowedActions: {
    canClose: true,
    canCreatePaymentAttempt: true,
    canReopen: false,
    canCancel: true,
    canResolvePendingAttempt: false,
  },
  ordersCount: 1,
  total: "136.00",
  currency: "mxn",
  orders: [sampleTableAccountOrder],
}

export const sampleOperationalTableSummaries = [
  {
    id: 301,
    externalId: "tbl-4",
    name: "Mesa 4",
    seats: 4,
    status: "occupied",
    statusLabel: "Ocupada",
    operationalStatus: "cooking",
    alert: true,
    // `alert` is derivable from these; the reason is what the card actually
    // renders, so a fixture with the flag and no reason shows an empty alert.
    alertReasons: ["ready_unserved"],
    activeOrdersCount: 2,
    activeBillsCount: 1,
    activeInvite: sampleActiveInvite,
    accountWorkspace: {
      billCount: 1,
      orderCount: 1,
      visibleBills: [sampleTableAccountBill],
    },
    actions: [
      {
        label: "Tomar comanda",
        value: "new_order",
        disabled: false,
        primary: true,
        kind: "navigate",
        href: "/tables?table_id=tbl-4",
      },
      {
        label: "Generar invitación",
        value: "invite",
        disabled: false,
        primary: false,
        kind: "invite",
        href: "/invites",
        tableExternalId: "tbl-4",
      },
    ],
  },
  {
    id: 302,
    externalId: "tbl-7",
    name: "Mesa 7",
    seats: 2,
    status: "available",
    statusLabel: "Disponible",
    operationalStatus: "free",
    alert: false,
    alertReasons: [],
    activeOrdersCount: 0,
    activeBillsCount: 0,
    activeInvite: null,
    accountWorkspace: {
      billCount: 0,
      orderCount: 0,
      visibleBills: [],
    },
    actions: [
      {
        label: "Sentar comensales",
        value: "seat",
        disabled: false,
        primary: true,
        kind: "event",
        href: "/tables/tbl-7",
        event: "seat",
      },
    ],
  },
]

export const sampleLocationOnboarding = {
  id: 42,
  name: "Menny Roma Norte",
  status: "active",
  statusLabel: "Activa",
  address: "Av. Álvaro Obregón 123, Roma Norte, Ciudad de México, CDMX, 06700, MX",
  addressLine1: "Av. Álvaro Obregón 123",
  addressLine2: "Roma Norte",
  city: "Ciudad de México",
  state: "CDMX",
  postalCode: "06700",
  country: "MX",
  latitude: "19.412000",
  longitude: "-99.163000",
  logoUrl: null,
  supportPhone: "+52 55 1234 5678",
  operationallySuspended: false,
  launchedAt: "2026-05-01T16:00:00.000Z",
  missingRequirements: [],
  missingRequirementLabels: [],
  readyForLaunch: true,
}

export const sampleBusinessHours = {
  id: 7,
  name: "business_hours",
  enabled: true,
  timeZone: "America/Mexico_City",
  periods: [1, 2, 3, 4, 5].map((dayOfWeek) => ({ dayOfWeek, startsAt: 540, endsAt: 1050 })),
}

export const sampleTimezones = [
  { value: "America/Mexico_City", label: "(GMT-06:00) Mexico City" },
  { value: "America/Monterrey", label: "(GMT-06:00) Monterrey" },
  { value: "America/Tijuana", label: "(GMT-08:00) Tijuana" },
]

export const samplePaymentMethods = {
  cash: true,
  physical_card_terminal: true,
  digital_in_app: true,
}

export const sampleStripeConnect = {
  status: "ready",
  ready: true,
  providerAccountId: "acct_storybook_123",
  chargesEnabled: true,
  payoutsEnabled: true,
  detailsSubmitted: true,
  requirementsCurrentlyDue: [],
  requirementsPastDue: [],
  disabledReason: null,
  requiredLocalFields: [],
  hasStripeHostedRequirements: false,
}

export const sampleMerchantProfile = {
  businessType: "company",
  businessName: "Menny Roma Norte",
  legalBusinessName: "Menny Roma Norte S.A. de C.V.",
  supportEmail: "soporte@menny.app",
  supportPhone: "+525555551234",
  statementDescriptor: "MENNY ROMA",
  businessUrl: "https://menny.app",
  productDescription: "Restaurante con órdenes y pagos digitales",
  addressLine1: "Av. Álvaro Obregón 123",
  addressLine2: "Local 2",
  addressCity: "Ciudad de México",
  addressState: "CDMX",
  addressPostalCode: "06700",
  addressCountry: "MX",
  representativeFirstName: "Mariana",
  representativeLastName: "López",
  representativeEmail: "mariana@menny.app",
  representativePhone: "+525555551234",
}

export const sampleBillingLocation = {
  id: 42,
  name: "Menny Roma Norte",
  status: "active",
  statusLabel: "Activa",
  paymentMethods: samplePaymentMethods,
  paymentMethodsConfirmed: true,
  stripeConnect: sampleStripeConnect,
  offlineCommissionPercentage: "1.0",
  digitalCommissionPercentage: "1.0",
  operationallySuspended: false,
  commissionInvoices: [
    {
      id: 901,
      periodStart: "2026-06-01",
      periodEnd: "2026-06-15",
      issueDate: "2026-06-16",
      dueDate: "2026-06-30",
      amount: "1240.00",
      status: "open",
      statusLabel: "Pendiente",
      reminderCount: 1,
      warningCount: 0,
      lastRemindedOn: "2026-06-22",
      lastWarnedOn: null,
    },
  ],
  merchantProfile: sampleMerchantProfile,
}

export const sampleRoleOptions = [
  { value: "manager", label: "Manager" },
  { value: "waiter", label: "Mesero" },
  { value: "bartender", label: "Barra" },
  { value: "cook", label: "Cocina" },
]

export const sampleManageUsers = [
  {
    id: 61,
    userId: 17,
    name: "Mariana López",
    email: "mariana@menny.app",
    role: "manager",
    roleLabel: "Manager",
  },
  {
    id: 62,
    userId: 18,
    name: "Nicolás Pérez",
    email: "nicolas@menny.app",
    role: "waiter",
    roleLabel: "Mesero",
  },
]

export const sampleFormUser = {
  id: 61,
  userId: 17,
  name: "Mariana López",
  emailAddress: "mariana@menny.app",
  role: "manager",
}

export const sampleStaffLocations = [
  {
    id: 42,
    name: "Menny Roma Norte",
    status: "active",
    current: true,
  },
  {
    id: 43,
    name: "Menny Condesa",
    status: "inactive",
    current: false,
  },
]

export const sampleOnboardingProgress = {
  steps: [
    { key: "address", label: "Dirección", href: "/manage/location", done: true },
    { key: "operating_hours", label: "Horarios de operación", href: "/manage/location", done: true },
    { key: "tables", label: "Mesas", href: "/manage/tables", done: true },
    { key: "menu", label: "Menú", href: "/manage/menu", done: true },
    { key: "stations", label: "Estaciones", href: "/manage/menu", done: true },
    { key: "payment_methods", label: "Al menos un método de pago habilitado", href: "/manage/billing/settings", done: false },
    { key: "payment_methods_confirmation", label: "Confirmación de métodos de pago para comensales", href: "/manage/billing/settings", done: false },
  ],
  completedCount: 5,
  totalCount: 7,
  progressPercentage: 71,
  readyForLaunch: false,
}

export const sampleStaffInvite = {
  id: 701,
  role: "waiter",
  roleLabel: "Mesero",
  restaurantName: "Menny Roma Norte",
}

// Inline SVG data-URI stands in for a real R2 photo so the photo card variant renders offline.
const SAMPLE_DISH_PHOTO =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop offset='0' stop-color='%23f0a294'/%3E%3Cstop offset='1' stop-color='%23a33220'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='100' height='100' fill='url(%23g)'/%3E%3Ccircle cx='50' cy='53' r='27' fill='%23ffffff' opacity='0.16'/%3E%3C/svg%3E"

// Covers the three dish-visual states: photo, staff emoji (future field), and icon fallback.
export const sampleUserMenuItems = [
  {
    id: 101,
    name: "Tacos al pastor",
    description: "Cerdo marinado al trompo, piña, cebolla y cilantro.",
    price: "68.00",
    currency: "mxn",
    station: "kitchen",
    status: "active",
    imageUrl: SAMPLE_DISH_PHOTO,
    emoji: null,
    extraGroups: [
      {
        id: 301,
        name: "Salsa",
        minSelect: 1,
        maxSelect: 1,
        extras: [
          { id: 201, name: "Verde", price: "0.00", status: "active" as const, imageUrl: null },
          { id: 202, name: "Roja", price: "0.00", status: "active" as const, imageUrl: null },
          { id: 203, name: "Habanero", price: "0.00", status: "sold_out" as const, imageUrl: null },
        ],
      },
      {
        id: 302,
        name: "Extras",
        minSelect: 0,
        maxSelect: 2,
        extras: [
          { id: 204, name: "Queso Oaxaca", price: "18.00", status: "active" as const, imageUrl: null },
          { id: 205, name: "Doble tortilla", price: "6.00", status: "active" as const, imageUrl: null },
        ],
      },
    ],
    categoryId: 2,
    categoryName: "Tacos",
  },
  {
    id: 103,
    name: "Guacamole tradicional",
    description: "Aguacate, cilantro, jitomate y totopos recién hechos.",
    price: "95.00",
    currency: "mxn",
    station: "kitchen",
    status: "active",
    imageUrl: null,
    emoji: "🥑",
    categoryId: 1,
    categoryName: "Entradas",
  },
  {
    id: 105,
    name: "Esquites",
    description: "Elote, chile piquín, queso y mayonesa.",
    price: "55.00",
    currency: "mxn",
    station: "kitchen",
    status: "active",
    imageUrl: null,
    emoji: "🌽",
    categoryId: 1,
    categoryName: "Entradas",
  },
  {
    id: 104,
    name: "Enchiladas verdes",
    description: "Pollo deshebrado, salsa verde, crema y queso fresco.",
    price: "145.00",
    currency: "mxn",
    station: "kitchen",
    status: "active",
    imageUrl: null,
    emoji: null,
    categoryId: 3,
    categoryName: "Fuertes",
  },
  {
    id: 107,
    name: "Molcajete mixto",
    description: "Res, pollo y arrachera en salsa de molcajete. Para compartir.",
    price: "320.00",
    currency: "mxn",
    station: "kitchen",
    status: "active",
    imageUrl: null,
    emoji: "🍲",
    categoryId: 3,
    categoryName: "Fuertes",
  },
  {
    id: 102,
    name: "Agua de jamaica",
    description: "Refrescante, sin azúcar agregada. 500 ml.",
    price: "42.00",
    currency: "mxn",
    station: "bar",
    status: "active",
    imageUrl: null,
    emoji: null,
    categoryId: 4,
    categoryName: "Bebidas",
  },
  {
    id: 108,
    name: "Horchata",
    description: "Arroz, canela y vainilla. 500 ml.",
    price: "45.00",
    currency: "mxn",
    station: "bar",
    status: "active",
    imageUrl: null,
    emoji: null,
    categoryId: 4,
    categoryName: "Bebidas",
  },
  {
    id: 106,
    name: "Flan napolitano",
    description: "Casero, con caramelo.",
    price: "65.00",
    currency: "mxn",
    station: "kitchen",
    status: "active",
    imageUrl: null,
    emoji: null,
    categoryId: 5,
    categoryName: "Postres",
  },
]

export const sampleDinerSummary = {
  locationName: "Menny Roma Norte",
  tableName: "Mesa 4",
  tableExternalId: "tbl-4",
  billId: 5001,
}

export const sampleRecentVisits = [
  {
    id: 91,
    locationName: "Menny Roma Norte",
    tableName: "Mesa 4",
    visitedAt: "2026-07-18T21:15:00.000Z",
    status: "closed",
    statusLabel: "Cerrada",
    total: "486.00",
    currency: "mxn",
    billId: 5001,
  },
  {
    id: 90,
    locationName: "Taquería El Califa",
    tableName: "Mesa 3",
    visitedAt: "2026-07-12T14:40:00.000Z",
    status: "closed",
    statusLabel: "Cerrada",
    total: "232.00",
    currency: "mxn",
    billId: 4890,
  },
  {
    id: 88,
    locationName: "Rosetta",
    tableName: null,
    visitedAt: "2026-06-29T20:05:00.000Z",
    status: "closed",
    statusLabel: "Para llevar",
    total: "375.00",
    currency: "mxn",
    billId: 4720,
  },
]

export const sampleUserMenuPayload = {
  // Chip data only, like the real payload: dishes live in the flat `items` list
  // below and the shell groups them with `groupMenuItemsByCategory`.
  categories: [
    { id: 1, name: "Entradas", emoji: "🥑" },
    { id: 2, name: "Tacos", emoji: "🌮" },
    { id: 3, name: "Fuertes", emoji: null },
    { id: 4, name: "Bebidas", emoji: "🥤" },
    { id: 5, name: "Postres", emoji: null },
  ],
  items: sampleUserMenuItems,
  // photo (101), staff emoji (107), icon fallback (104)
  featuredItems: [sampleUserMenuItems[0], sampleUserMenuItems[4], sampleUserMenuItems[3]],
}

export const sampleUserItemDetail = {
  ...sampleUserMenuItems[0],
  extraGroups: sampleUserMenuItems[0].extraGroups,
}

// Emoji visual, no photo, no extras — the simpler dish-detail case.
export const sampleUserItemDetailEmoji = {
  ...sampleUserMenuItems[1],
  extraGroups: [],
}

export const sampleBillOrders = [
  {
    id: 7001,
    name: "Taco pastor",
    quantity: 2,
    notes: "Sin cebolla",
    modifiers: [{ id: 201, name: "Queso Oaxaca", price: "18.00" }],
    paymentStatus: "unpaid",
    station: "kitchen",
    status: "in_progress",
    statusLabel: "En cocina",
    serviceType: "eat_in",
    unitPrice: "68.00",
    lineTotal: "136.00",
    whoName: "Lucía",
    isMine: true,
    itemId: 101,
  },
  {
    id: 7002,
    name: "Agua de jamaica",
    quantity: 1,
    notes: null,
    modifiers: [],
    paymentStatus: "unpaid",
    station: "bar",
    status: "ready",
    statusLabel: "Lista",
    serviceType: "take_out",
    unitPrice: "42.00",
    lineTotal: "42.00",
    whoName: "Nicolás",
    isMine: false,
    itemId: 102,
  },
]

export const sampleUserBill = {
  id: 5001,
  status: "open",
  currency: "mxn",
  subtotal: "178.00",
  tip: "18.00",
  total: "196.00",
  tableName: "Mesa 4",
  locationName: "Menny Roma Norte",
  // Open, so nothing has settled: no payment to date and no method to name.
  // The paid variant below is what the receipt story renders.
  paidAt: null,
  timeZone: "America/Mexico_City",
  paymentMethods: [] as { method: string; label: string }[],
  itemCount: 3,
  enabledPaymentMethods: ["cash", "physical_card_terminal", "digital_in_app"],
  tipPercentages: [0, 5, 10, 15, 20],
  pendingPaymentAttempt: null,
  orders: sampleBillOrders,
  participants: [
    { id: 1, name: "Lucía", initials: "L", isMine: true },
    { id: 2, name: "Nicolás", initials: "N", isMine: false },
  ],
}

// Pickup context: solo order, no table, no shared participants.
export const sampleDinerPickup = {
  locationName: "Taquería El Califa",
  tableName: null,
  tableExternalId: null,
  billId: 5202,
  serviceContext: "pickup" as const,
}

export const sampleUserBillPickup = {
  id: 5202,
  status: "open",
  currency: "mxn",
  subtotal: "181.00",
  tip: "0.00",
  total: "181.00",
  tableName: null,
  locationName: "Taquería El Califa",
  paidAt: null,
  timeZone: "America/Mexico_City",
  paymentMethods: [] as { method: string; label: string }[],
  itemCount: 3,
  enabledPaymentMethods: ["cash", "digital_in_app"],
  tipPercentages: [0, 5, 10, 15, 20],
  pendingPaymentAttempt: null,
  orders: [
    {
      id: 7101,
      name: "Tacos al pastor",
      quantity: 2,
      notes: null,
      modifiers: [],
      paymentStatus: "unpaid",
      station: "kitchen",
      status: "pending",
      statusLabel: "Pendiente",
      serviceType: "pick_up",
      unitPrice: "68.00",
      lineTotal: "136.00",
      whoName: "Ángel",
      isMine: true,
      itemId: 101,
    },
    {
      id: 7102,
      name: "Agua de horchata",
      quantity: 1,
      notes: null,
      modifiers: [],
      paymentStatus: "unpaid",
      station: "bar",
      status: "pending",
      statusLabel: "Pendiente",
      serviceType: "pick_up",
      unitPrice: "45.00",
      lineTotal: "45.00",
      whoName: "Ángel",
      isMine: true,
      itemId: 108,
    },
  ],
  participants: [{ id: 1, name: "Ángel", initials: "Á", isMine: true }],
}

export const sampleTrackingStages = [
  { id: "received", label: "Recibida", state: "done" },
  { id: "confirmed", label: "Confirmada", state: "done" },
  { id: "in_progress", label: "En cocina", state: "active" },
  { id: "ready", label: "Lista", state: "pending" },
]

export const sampleUserOrderTracking = {
  id: 7001,
  name: "Taco pastor",
  status: "in_progress",
  statusLabel: "En preparación",
  serviceType: "eat_in",
  serviceTypeLabel: "Mesa",
  station: "kitchen",
  quantity: 2,
  notes: "Sin cebolla",
  paymentStatus: "unpaid",
  modifiers: [{ id: 201, name: "Queso Oaxaca", price: "18.00" }],
  pickupEtaMinutes: null,
  paidAt: null,
  unitPrice: "68.00",
  lineTotal: "136.00",
  itemId: 101,
  billId: 5001,
  tableName: "Mesa 4",
  whoName: "Lucía",
  createdAt: fixedNow,
  trackingStages: sampleTrackingStages,
}

export const samplePickupLocations = [
  { id: 42, name: "Menny Roma Norte" },
  { id: 43, name: "Menny Condesa" },
]

export const samplePickupItemsByLocation = {
  "42": [
    { id: 101, name: "Taco pastor", price: "68.00", currency: "mxn" },
    { id: 102, name: "Agua de jamaica", price: "42.00", currency: "mxn" },
  ],
  "43": [{ id: 103, name: "Café de olla", price: "48.00", currency: "mxn" }],
}

export const sampleActivePickups = [
  {
    id: 7301,
    locationName: "Menny Roma Norte",
    status: "ready_for_pickup",
    statusLabel: "Listo para retirar",
    billId: 5101,
    etaMinutes: 8,
    paymentStatus: "unpaid",
    enabledPaymentMethods: ["cash", "digital_in_app"],
  },
]

export const sampleDirectoryResults = [
  {
    id: 42,
    name: "Cantina Roma Norte",
    address: "Av. Álvaro Obregón 123, Roma Norte",
    openNow: true,
    enabledPaymentMethods: ["cash", "digital_in_app"],
    latitude: 19.41,
    longitude: -99.16,
    logoUrl: null,
    distanceKm: null,
  },
  {
    id: 43,
    name: "Taquería El Califa",
    address: "Altata 22, Hipódromo Condesa",
    openNow: true,
    enabledPaymentMethods: ["cash"],
    latitude: 19.4,
    longitude: -99.17,
    logoUrl: null,
    distanceKm: null,
  },
  {
    id: 44,
    name: "Rosetta",
    address: "Colima 166, Roma Norte",
    openNow: false,
    enabledPaymentMethods: ["digital_in_app"],
    latitude: 19.42,
    longitude: -99.16,
    logoUrl: null,
    distanceKm: null,
  },
]

// Same restaurants as returned when the diner shares their location: ordered by
// distance, each carrying a distanceKm.
export const sampleDirectoryResultsNearby = [
  { ...sampleDirectoryResults[1], distanceKm: 0.4 },
  { ...sampleDirectoryResults[0], distanceKm: 1.2 },
  { ...sampleDirectoryResults[2], distanceKm: 2.8 },
]

export const sampleInvitePayload = {
  token: "invite-storybook",
  locationName: "Menny Roma Norte",
  tableName: "Mesa 4",
  stateMessage: "Tu mesa ya tiene una cuenta activa.",
  activeBills: [
    {
      id: 5001,
      status: "open",
      currentUserCanPay: true,
      pendingPaymentAttempt: null,
      participants: [
        { id: 1, name: "Lucía", initials: "L", isMine: false },
        { id: 2, name: "Nicolás", initials: "N", isMine: false },
        { id: 3, name: "Ángel", initials: "Á", isMine: true },
      ],
      orders: [
        {
          id: 7001,
          name: "Taco pastor",
          quantity: 2,
          paymentStatus: "unpaid",
          lineTotal: "136.00",
          currency: "mxn",
        },
      ],
    },
  ],
}
